import { useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { api, errorMessage } from '@/lib/api/client';
import type { Customer, GiftCard, GiftCardBatch } from '@/lib/api/types';
import { money, num } from '@/lib/format';
import { customerKey } from '@/features/customers/keys';
import { Button } from '@/ui/Button';
import { CustomerPicker } from '@/ui/CustomerPicker';
import { ConfirmDialog } from '@/ui/Dialog';
import { Field, Input, MoneyInput, Select, Textarea } from '@/ui/Field';
import { Page, PageBody, PageHeader, Panel, Stat, StatRow } from '@/ui/Panel';

export const Route = createFileRoute('/_app/gift-cards_/batches_/new')({
  staticData: { crumb: 'New batch' },
  component: NewBatchScreen,
});

type ChargeMethod = 'none' | 'house_account' | 'invoice';

/** One line per card, blanks dropped — the API wants either none or exactly N. */
function lines(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function NewBatchScreen() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [name, setName] = useState('');
  const [quantityRaw, setQuantityRaw] = useState('');
  const [faceValue, setFaceValue] = useState<number | null>(null);
  const [discount, setDiscount] = useState<number | null>(null);
  const [chargeMethod, setChargeMethod] = useState<ChargeMethod>('none');
  const [chargeCustomer, setChargeCustomer] = useState<Customer | null>(null);
  const [notes, setNotes] = useState('');
  const [labelsRaw, setLabelsRaw] = useState('');
  const [phonesRaw, setPhonesRaw] = useState('');
  const [confirming, setConfirming] = useState(false);

  const quantity = Number(quantityRaw);
  const quantityValid = Number.isInteger(quantity) && quantity >= 1 && quantity <= 5000;
  const faceValid = faceValue !== null && faceValue > 0;
  const cardLabels = lines(labelsRaw);
  const cardPhones = lines(phonesRaw);
  const labelsValid = cardLabels.length === 0 || cardLabels.length === quantity;
  const phonesValid = cardPhones.length === 0 || cardPhones.length === quantity;
  const customerRequired = chargeMethod === 'house_account';
  const customerValid = !customerRequired || !!chargeCustomer;

  const totalFaceValue = quantityValid && faceValid ? quantity * (faceValue ?? 0) : 0;
  const discountCents = discount ?? 0;
  const charged = totalFaceValue - discountCents;
  const discountValid = discountCents >= 0 && discountCents <= totalFaceValue;

  const valid =
    name.trim().length > 0 &&
    quantityValid &&
    faceValid &&
    discountValid &&
    labelsValid &&
    phonesValid &&
    customerValid;

  const create = useMutation({
    mutationFn: () =>
      api.post<{ batch: GiftCardBatch; cards: GiftCard[] }>('/gift-card-batches', {
        name: name.trim(),
        quantity,
        faceValueCents: faceValue ?? 0,
        discountCents,
        chargeMethod,
        ...(chargeCustomer ? { chargeCustomerId: Number(chargeCustomer.id) } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        ...(cardLabels.length ? { cardLabels } : {}),
        ...(cardPhones.length ? { cardPhones } : {}),
      }),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['gift-card-batches'] });
      void qc.invalidateQueries({ queryKey: ['gift-cards'] });
      if (chargeCustomer) {
        void qc.invalidateQueries({ queryKey: customerKey(chargeCustomer.id) });
        void qc.invalidateQueries({ queryKey: ['customers'] });
      }
      toast.success(
        `${num(result.cards.length)} cards created in “${result.batch.name}”.`
      );
      void navigate({
        to: '/gift-cards/batches/$id',
        params: { id: String(result.batch.id) },
      });
    },
    onError: (err) => {
      setConfirming(false);
      toast.error(errorMessage(err));
    },
  });

  return (
    <Page>
      <PageHeader
        eyebrow="Gift cards"
        title="New batch"
        subtitle="Generates every card, and posts the charge, in one transaction"
        actions={
          <>
            <Button
              asChild
              variant="ghost"
              size="sm"
              icon={<ArrowLeft className="size-3.5" />}
            >
              <Link to="/gift-cards/batches" search={{ page: 1 }}>
                Cancel
              </Link>
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={<Layers className="size-3.5" />}
              disabled={!valid}
              onClick={() => setConfirming(true)}
            >
              Review batch
            </Button>
          </>
        }
      />
      <PageBody>
        <div className="mx-auto flex max-w-4xl flex-col gap-3">
          <StatRow>
            <Stat label="Cards" value={quantityValid ? num(quantity) : '—'} />
            <Stat label="Face value each" value={faceValid ? money(faceValue) : '—'} />
            <Stat
              label="Total face value"
              value={totalFaceValue ? money(totalFaceValue) : '—'}
            />
            <Stat
              label="Charged"
              value={totalFaceValue ? money(charged) : '—'}
              sub={
                chargeMethod === 'none'
                  ? 'no charge recorded'
                  : chargeMethod.replace('_', ' ')
              }
              tone={chargeMethod === 'house_account' ? 'warn' : undefined}
            />
          </StatRow>

          <div className="grid gap-3 md:grid-cols-2">
            <Panel title="Batch">
              <div className="flex flex-col gap-3">
                <Field
                  label="Name"
                  required
                  hint="Shows on the batch list and each card's history."
                >
                  <Input
                    autoFocus
                    value={name}
                    onChange={(e) => setName(e.currentTarget.value)}
                    maxLength={255}
                    placeholder="e.g. Ramadan 2026 corporate"
                  />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    label="Quantity"
                    required
                    error={quantityRaw && !quantityValid ? '1 to 5000.' : null}
                  >
                    <Input
                      mono
                      inputMode="numeric"
                      value={quantityRaw}
                      invalid={!!quantityRaw && !quantityValid}
                      onChange={(e) =>
                        setQuantityRaw(e.currentTarget.value.replace(/\D/g, ''))
                      }
                    />
                  </Field>
                  <Field label="Face value" required hint="Balance on each card.">
                    <MoneyInput value={faceValue} onValueChange={setFaceValue} />
                  </Field>
                </div>
                <Field
                  label="Discount"
                  hint="Taken off the amount charged, not off the cards."
                  error={!discountValid ? 'Cannot exceed the total face value.' : null}
                >
                  <MoneyInput value={discount} onValueChange={setDiscount} />
                </Field>
                <Field label="Notes">
                  <Textarea
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.currentTarget.value)}
                    placeholder="Who ordered it, what it is for."
                  />
                </Field>
              </div>
            </Panel>

            <Panel title="Charge">
              <div className="flex flex-col gap-3">
                <Field label="Method">
                  <Select
                    value={chargeMethod}
                    onChange={(e) =>
                      setChargeMethod(e.currentTarget.value as ChargeMethod)
                    }
                  >
                    <option value="none">No charge recorded</option>
                    <option value="house_account">House account</option>
                    <option value="invoice">Invoice</option>
                  </Select>
                </Field>
                {chargeMethod === 'house_account' ? (
                  <Field
                    label="Charge to"
                    required
                    hint="A ledger charge is posted to this account in the same transaction that creates the cards."
                  >
                    <CustomerPicker value={chargeCustomer} onChange={setChargeCustomer} />
                  </Field>
                ) : (
                  <p className="text-2xs text-ink-faint leading-relaxed">
                    {chargeMethod === 'invoice'
                      ? 'Recorded as invoiced. Nothing is posted to a house account here.'
                      : 'Nothing is charged anywhere. Take payment separately.'}
                  </p>
                )}

                <div className="border-line-soft border-t pt-3">
                  <Field
                    label="Card names"
                    hint={`Optional, one per line. Leave empty or give exactly ${quantityValid ? num(quantity) : 'quantity'} lines.`}
                    error={
                      labelsValid
                        ? null
                        : `${cardLabels.length} names for ${quantity} cards.`
                    }
                  >
                    <Textarea
                      rows={4}
                      value={labelsRaw}
                      onChange={(e) => setLabelsRaw(e.currentTarget.value)}
                      invalid={!labelsValid}
                    />
                  </Field>
                </div>
                <Field
                  label="Recipient phones"
                  hint="Optional, one per line, same count rule. Used for WhatsApp delivery."
                  error={
                    phonesValid
                      ? null
                      : `${cardPhones.length} phones for ${quantity} cards.`
                  }
                >
                  <Textarea
                    rows={4}
                    value={phonesRaw}
                    onChange={(e) => setPhonesRaw(e.currentTarget.value)}
                    invalid={!phonesValid}
                  />
                </Field>
              </div>
            </Panel>
          </div>
        </div>
      </PageBody>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Create this batch?"
        description="Every card becomes spendable immediately. There is no bulk undo."
        confirmLabel="Create batch"
        variant="primary"
        typeToConfirm={charged >= 100_000 ? 'CREATE' : undefined}
        loading={create.isPending}
        consequences={[
          `Creates ${num(quantity)} cards of ${money(faceValue)} each`,
          `Total face value ${money(totalFaceValue)}`,
          chargeMethod === 'house_account' && chargeCustomer
            ? `Posts a ${money(charged)} charge to ${chargeCustomer.name} — balance ${money(chargeCustomer.balance)} → ${money(chargeCustomer.balance + charged)}`
            : chargeMethod === 'invoice'
              ? `Records ${money(charged)} as invoiced — no ledger entry`
              : 'No charge is recorded anywhere',
          ...(cardLabels.length ? [`Names ${num(cardLabels.length)} cards`] : []),
          ...(cardPhones.length
            ? [`Sets ${num(cardPhones.length)} recipient phones`]
            : []),
        ]}
        onConfirm={() => create.mutate()}
      />
    </Page>
  );
}
